import { AgentBookingModel } from '@/__generated';
import { useCurrencyFormat } from '@/hooks/useCurrencyFormat';
import React from 'react';
import moment from 'moment';
import DefaultCancellationPolicy from '@/pages/HotelDetail/components/DefaultCancellationPolicy';
import { HOTEL_CONFIG, appDisplayDateFormat } from '@/config';
import HotelJapaneseName from '@/components/App/Hotels/HotelJapaneseName';
import HotelStar from '@/components/App/Hotels/HotelStar';
import SingleOrPluralText from '@/components/App/SingleOrPluralText';
import { HotelRoomStatus } from '@/constants';

type Props = {
  booking: AgentBookingModel;
  onPayment?: () => void;
  showSubmitButtons?: boolean;
};

export default function HotelSummary({ booking, onPayment }: Props) {
  const selectedHotel = booking.hotel;
  const totalPrice = booking.sellingPrice;

  const { formatCurrency } = useCurrencyFormat();
  const numberOfNight = React.useMemo(() => {
    return moment(booking?.checkoutDate).diff(moment(booking?.checkinDate), 'day');
  }, [booking]);

  const hotelImage = React.useMemo(() => {
    if (selectedHotel?.images?.length) {
      return selectedHotel?.images[0];
    }
    return HOTEL_CONFIG.defaultImage;
  }, [selectedHotel]);

  const rooms = React.useMemo(() => {
    return selectedHotel?.rooms?.filter(x => x.status === HotelRoomStatus.Booked);
  }, [selectedHotel?.rooms]);
  return (
    <div className="px-30 py-30 border-light rounded-4">
      <div className="text-20 fw-500 mb-20">Your booking details</div>
      <div className="row x-gap-15 y-gap-20 mb-10">
        <div className="col-md-4 col-xs-12">
          <img className="w-100 rounded-4 object-cover" src={hotelImage} />
        </div>
        <div className="col">
          <div className="d-flex x-gap-5">
            <HotelStar star={selectedHotel?.starRating || 0} />
          </div>
          <div className="lh-17 fw-500">
            {selectedHotel?.hotelName}
            <HotelJapaneseName name={selectedHotel?.hotelNameJp} addBefore="-" />
          </div>
        </div>
      </div>
      <div className="text-14 lh-15 mt-5">{selectedHotel?.address}</div>

      <div className="border-top-light mt-10 mb-20" />
      <div className="row y-gap-20 justify-between">
        <div className="col-auto">
          <div className="text-15">Check-in</div>
          <div className="fw-500">{appDisplayDateFormat.default.dateOnly(booking.checkinDate)}</div>
          {selectedHotel?.checkinTime ? <div className="text-15 text-light-1">{selectedHotel?.checkinTime}</div> : null}
        </div>
        <div className="col-auto md:d-none">
          <div className="h-full w-1 bg-border" />
        </div>
        <div className="col-auto text-right md:text-left">
          <div className="text-15">Check-out</div>
          <div className="fw-500">{appDisplayDateFormat.default.dateOnly(booking.checkoutDate)}</div>
          {selectedHotel?.checkoutTime ? <div className="text-15 text-light-1">{selectedHotel?.checkoutTime}</div> : null}
        </div>
      </div>
      {/* End row */}

      <div className="border-top-light mt-10 mb-10" />
      <div>
        <div className="text-15">Total length of stay:</div>
        <div className="fw-500">
          <SingleOrPluralText count={numberOfNight} singleText="{} night" pluralText="{} nights" />
        </div>
      </div>

      <div className="border-top-light mt-10 mb-10" />
      <div className="text-15">Room booked:</div>
      {rooms?.map(room => {
        return (
          <div className="row  justify-between items-center" key={room.id}>
            <div className="col fw-500">{room.roomTypeName}</div>
            <div className="col-auto">
              <div className="text-15 fw-500">
                <SingleOrPluralText count={room.noOfRooms || 0} singleText="{} room" pluralText="{} rooms" />
              </div>
            </div>
            <div className="col-12 pb-10">
              <div className="border-bottom-light pb-10">
                <DefaultCancellationPolicy checkinDate={booking.checkinDate} plan={room as any} />
              </div>
            </div>
          </div>
        );
      })}
      <div className="text-15">Total price</div>
      <div className="text-center">
        <span className="fw-500 text-22 text-blue-1">{formatCurrency(totalPrice)}</span>
      </div>

      {onPayment ? (
        <button onClick={onPayment} className="button h-60 w-100 mt-10 px-24 -dark-1 bg-red-1 text-white text-22">
          Place Booking
          <i className="icon-arrow-top-right ml-15" />
        </button>
      ) : null}
    </div>
  );
}
